/**
 * 역할(Role) 관련 타입 정의
 *
 * nGle-Works ERP의 역할 기반 접근 제어(RBAC) 시스템에서 사용되는
 * 역할 관련 타입들을 정의합니다.
 */

import { Timestamp } from 'firebase/firestore';

/**
 * 역할 ID 유니온 타입
 *
 * nGle-Works에서 사용하는 역할 (V111 업데이트):
 *
 * **활성 역할:**
 * - role_te: 일반 직원
 * - role_tl: 테스트 리더 (QA)
 * - role_manager: 관리자 (팀장/실장/본부장)
 * - role_hr: 인사 플래그
 * - role_exec: 경영진 열람
 * - system_admin: 시스템 관리자
 *
 * **기존 역할 (Deprecated, 하위 호환성):**
 * - general, tl, org_manager, approver, executive
 *
 * **V111 제거됨:**
 * - super_admin → system_admin 사용
 * - hr → role_hr 사용
 * - hr_admin → role_hr 사용
 */
export type RoleId =
  // 활성 역할 (V111)
  | 'role_te'
  | 'role_tl'
  | 'role_manager'
  | 'role_hr'
  | 'role_exec'
  | 'system_admin'
  // V27: 회사 스코프 어드민 역할
  | 'role_admin_kr'
  | 'role_admin_cn'
  // V29.14: 글로벌 스코프 역할
  | 'role_global'
  // 기존 역할 (Deprecated, 하위 호환성 - super_admin/hr/hr_admin 제외)
  | 'general'
  | 'tl'
  | 'org_manager'
  | 'approver'
  | 'executive';

/**
 * 역할 정보 인터페이스
 *
 * Firestore auth_roles 컬렉션의 문서 구조를 정의합니다.
 * 각 역할은 시스템에서 고정된 역할로 관리되며, 삭제할 수 없습니다.
 *
 * @example
 * // auth_roles/general 문서
 * {
 *   id: 'general',
 *   name: '일반 직원',
 *   description: '기본 직원 역할. 본인의 근태/연차 관리 가능',
 *   isSystem: true,
 *   isAssignableFromUI: true,
 *   sortOrder: 1,
 *   createdAt: Timestamp,
 *   updatedAt: Timestamp
 * }
 */
export interface Role {
  /**
   * 역할 고유 ID
   * RoleId 타입의 값만 가능
   *
   * @example 'general', 'role_hr', 'system_admin'
   */
  id: RoleId;

  /**
   * 역할의 한국어 이름
   * UI에 표시되는 이름
   *
   * @example '일반 직원', '인사관리자', '시스템 관리자'
   */
  name: string;

  /**
   * 역할에 대한 상세 설명
   * 역할이 가진 권한과 책임을 설명
   *
   * @example '기본 직원 역할. 본인의 근태/연차 관리 가능'
   */
  description: string;

  /**
   * 시스템 기본 역할 여부
   * true인 경우 삭제할 수 없음
   * 모든 역할은 시스템 역할이므로 항상 true
   */
  isSystem: boolean;

  /**
   * UI에서 할당 가능 여부
   * system_admin은 false (보안상 UI에서 직접 할당 불가)
   * 나머지 역할은 true
   */
  isAssignableFromUI: boolean;

  /**
   * UI 표시 순서
   * 작은 숫자가 먼저 표시됨
   *
   * @example 1, 2, 3, ...
   */
  sortOrder: number;

  /**
   * 역할 생성 일시
   */
  createdAt: Timestamp;

  /**
   * 역할 최종 수정 일시
   */
  updatedAt: Timestamp;
}

/**
 * V29: 관리자급 역할 그룹
 *
 * 프론트엔드에서 관리자 판정 시 사용합니다.
 * 백엔드의 roles.ts ADMIN_LIKE_ROLES와 동일하게 유지해야 합니다.
 *
 * - system_admin: 시스템 관리자 (전체 회사)
 * - role_admin_kr: KR 회사 관리자
 * - role_admin_cn: CN 회사 관리자
 *
 * @example
 * import { ADMIN_LIKE_ROLES } from './role';
 * const isAdmin = ADMIN_LIKE_ROLES.includes(roleId);
 */
export const ADMIN_LIKE_ROLES = [
  'system_admin',
  'role_admin_kr',
  'role_admin_cn',
] as const;

export type AdminLikeRoleId = typeof ADMIN_LIKE_ROLES[number];

/**
 * 조직 범위 역할 목록
 *
 * 이 역할들은 특정 조직에 대해서만 권한을 가집니다.
 * 역할 할당 시 orgUnitId가 필수입니다.
 *
 * **역할 시스템 업데이트 (2025-12-08):**
 * - 신규: role_manager (org_manager 대체)
 * - 기존: org_manager, approver (하위 호환성)
 */
export const ORG_SCOPED_ROLES: RoleId[] = [
  'role_manager',  // 신규
  'org_manager',   // Deprecated
  'approver'
];

/**
 * 조직 범위 역할 확인 함수
 *
 * 주어진 역할이 조직 범위 역할인지 확인합니다.
 * 조직 범위 역할은 역할 할당 시 orgUnitId가 필수입니다.
 *
 * @param roleId - 확인할 역할 ID
 * @returns 조직 범위 역할이면 true, 아니면 false
 *
 * @example
 * isOrgScopedRole('org_manager') // true
 * isOrgScopedRole('role_hr') // false
 * isOrgScopedRole('approver') // true
 */
export function isOrgScopedRole(roleId: RoleId): boolean {
  return ORG_SCOPED_ROLES.includes(roleId);
}

/**
 * 역할 정보 맵
 *
 * 각 역할의 기본 정보를 담고 있는 객체입니다.
 * UI에서 역할 정보를 표시할 때 사용합니다.
 *
 * @example
 * const roleName = ROLE_INFO['general'].name; // '일반 직원'
 * const roleDesc = ROLE_INFO['role_hr'].description; // 'HR 메뉴 접근 플래그...'
 */
export const ROLE_INFO: Record<RoleId, Omit<Role, 'createdAt' | 'updatedAt'>> = {
  // ========================================
  // 신규 역할 (2025-12-08)
  // ========================================
  role_te: {
    id: 'role_te',
    name: '일반 직원',
    description: '기본 직원 역할. 출퇴근, 휴가 신청 등 개인 업무 수행',
    isSystem: true,
    isAssignableFromUI: true,
    sortOrder: 1,
  },
  role_tl: {
    id: 'role_tl',
    name: '테스트 리더',
    description: 'QA 테스트 리더. 일반 직원 권한 + MM 작성/관리 권한',
    isSystem: true,
    isAssignableFromUI: true,
    sortOrder: 2,
  },
  role_manager: {
    id: 'role_manager',
    name: '관리자',
    description: '팀장/실장/본부장 역할. 일반 직원 권한 + 승인 권한 + 팀 관리',
    isSystem: true,
    isAssignableFromUI: true,
    sortOrder: 3,
  },
  role_hr: {
    id: 'role_hr',
    name: '인사 플래그',
    description: 'HR 메뉴 접근 플래그. TE 또는 Manager와 조합하여 사용',
    isSystem: true,
    isAssignableFromUI: true,
    sortOrder: 4,
  },
  role_exec: {
    id: 'role_exec',
    name: '경영진 열람',
    description: '이사/대표 등 경영진 전용. HR/조직 데이터 열람 전용 (수정 불가)',
    isSystem: true,
    isAssignableFromUI: true,
    sortOrder: 5,
  },
  system_admin: {
    id: 'system_admin',
    name: '시스템 관리자',
    description: '시스템 전체 관리자. 모든 권한 보유',
    isSystem: true,
    isAssignableFromUI: false,
    sortOrder: 6,
  },

  // ========================================
  // 기존 역할 (Deprecated, 하위 호환성)
  // ========================================
  general: {
    id: 'general',
    name: '일반 직원 (deprecated)',
    description: '⚠️ 비활성화된 역할. role_te 사용 권장',
    isSystem: true,
    isAssignableFromUI: false,
    sortOrder: 101,
  },
  tl: {
    id: 'tl',
    name: 'TL (deprecated)',
    description: '⚠️ 비활성화된 역할. role_tl 사용 권장',
    isSystem: true,
    isAssignableFromUI: false,
    sortOrder: 102,
  },
  org_manager: {
    id: 'org_manager',
    name: '조직장 (deprecated)',
    description: '⚠️ 비활성화된 역할. role_manager 사용 권장',
    isSystem: true,
    isAssignableFromUI: false,
    sortOrder: 103,
  },
  approver: {
    id: 'approver',
    name: '승인자 (deprecated)',
    description: '⚠️ 비활성화된 역할. role_manager 사용 권장',
    isSystem: true,
    isAssignableFromUI: false,
    sortOrder: 105,
  },
  executive: {
    id: 'executive',
    name: '임원 (deprecated)',
    description: '⚠️ 비활성화된 역할. role_exec 사용 권장',
    isSystem: true,
    isAssignableFromUI: false,
    sortOrder: 106,
  },

  // ========================================
  // V27: 회사 스코프 어드민 역할
  // ========================================
  role_admin_kr: {
    id: 'role_admin_kr',
    name: 'KR 회사 관리자',
    description: 'KR(한국) 회사 스코프 고정 관리자. system_admin급 기능 권한 + KR 회사만 접근',
    isSystem: true,
    isAssignableFromUI: false,
    sortOrder: 7,
  },
  role_admin_cn: {
    id: 'role_admin_cn',
    name: 'CN 회사 관리자',
    description: 'CN(중국) 회사 스코프 고정 관리자. system_admin급 기능 권한 + CN 회사만 접근',
    isSystem: true,
    isAssignableFromUI: false,
    sortOrder: 8,
  },

  // ========================================
  // V29.14: 글로벌 스코프 역할
  // ========================================
  role_global: {
    id: 'role_global',
    name: 'Global HR Access',
    description: '회사 스코프를 ALL(전체)로 확장. 기능 권한은 별도 역할(role_hr 등)에 의존',
    isSystem: true,
    isAssignableFromUI: false, // system_admin만 할당 가능
    sortOrder: 9,
  },
  // V111: super_admin, hr, hr_admin 레거시 roleId 완전 제거
};
